// =====================================================================
// PatientPreviewDialog (PME Sprint 2 / S51 · enriquecido S52 #49) — preview
// da ficha do paciente num modal. "Prancheta de bolso": foto real +
// identificação + ALERGIAS + anamnese (instantâneo, dado da linha) + últimas
// consultas + agenda (carregados ao abrir). Pra editar/ver tudo -> prontuário.
// =====================================================================
import React, { useState, useEffect } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  Chip,
  Paper,
  Box,
  CircularProgress,
  Divider
} from "@material-ui/core";
import WarningIcon from "@material-ui/icons/Warning";
import CheckCircleOutlineIcon from "@material-ui/icons/CheckCircleOutline";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import DescriptionIcon from "@material-ui/icons/Description";
import EventNoteIcon from "@material-ui/icons/EventNote";
import PatientPhoto from "./PatientPhoto";

import {
  listEncounters,
  listPatientAppointments,
  listEncounterProfessionals,
  ENCOUNTER_TYPE_LABELS,
  ENCOUNTER_STATUS_LABELS
} from "../../services/encounterService";
import useIsMobile from "../../utils/useIsMobile";

const useStyles = makeStyles((theme) => ({
  header: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2.5, 3),
    background: `linear-gradient(135deg, ${theme.palette.primary.main}22 0%, transparent 70%)`,
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  name: { fontWeight: 700 },
  chips: { display: "flex", gap: theme.spacing(1), marginTop: 4, flexWrap: "wrap" },
  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(0.5, 2),
    color: theme.palette.text.secondary,
    fontSize: 14,
    padding: theme.spacing(1.5, 3, 0)
  },
  allergy: {
    margin: theme.spacing(1.5, 3),
    padding: theme.spacing(1.25, 2),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1.5),
    background: "rgba(220,38,38,0.10)",
    border: `1px solid ${theme.palette.error.main}`,
    borderLeft: `5px solid ${theme.palette.error.main}`
  },
  noAllergy: {
    margin: theme.spacing(1.5, 3),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    color: theme.palette.text.secondary
  },
  anamnese: { padding: theme.spacing(0, 3, 1), overflowX: "hidden" },
  fieldLabel: {
    fontSize: 11,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: theme.palette.text.secondary,
    fontWeight: 600
  },
  fieldValue: { fontSize: 14.5, whiteSpace: "pre-wrap" },
  empty: { color: theme.palette.text.secondary, fontStyle: "italic" },
  sectionTitle: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    fontWeight: 600,
    margin: theme.spacing(1.5, 0, 1)
  },
  miniRow: {
    padding: theme.spacing(0.75, 1.25),
    marginBottom: theme.spacing(0.75),
    borderRadius: 4,
    border: `1px solid ${theme.palette.divider}`
  },
  miniTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    flexWrap: "wrap"
  },
  miniMeta: { color: theme.palette.text.secondary, fontSize: 12.5 }
}));

const pad = (n) => String(n).padStart(2, "0");

const computeAge = (birthDate) => {
  if (!birthDate) return null;
  const b = new Date(birthDate);
  if (isNaN(b.getTime())) return null;
  const now = new Date();
  let age = now.getFullYear() - b.getFullYear();
  const m = now.getMonth() - b.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < b.getDate())) age -= 1;
  return age >= 0 && age < 130 ? age : null;
};

const formatCpf = (cpf) => {
  if (!cpf) return "—";
  const d = String(cpf).replace(/\D/g, "");
  if (d.length !== 11) return cpf;
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).slice(0, 10).split("-");
  return y && m && d ? `${d}/${m}/${y}` : "—";
};

const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return "—";
  return `${pad(dt.getDate())}/${pad(dt.getMonth() + 1)}/${dt.getFullYear()} ${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
};

const habitsSummary = (h) => {
  if (!h || typeof h !== "object") return "";
  const parts = [];
  if (h.smoking) parts.push(`Tabagismo: ${h.smoking}`);
  if (h.alcohol) parts.push(`Álcool: ${h.alcohol}`);
  if (h.exercise) parts.push(`Exercício: ${h.exercise}`);
  if (h.sleep) parts.push(`Sono: ${h.sleep}`);
  return parts.join(" · ");
};

const apptStatusLabel = {
  scheduled: "Agendado",
  confirmed: "Confirmado",
  completed: "Realizado",
  cancelled: "Cancelado",
  no_show: "Não compareceu"
};

const Field = ({ label, value, classes, full }) => {
  if (!value || !String(value).trim()) return null;
  return (
    <Grid item xs={12} sm={full ? 12 : 6}>
      <div className={classes.fieldLabel}>{label}</div>
      <div className={classes.fieldValue}>{value}</div>
    </Grid>
  );
};

const PatientPreviewDialog = ({ patient, open, onClose, onOpenFull }) => {
  const classes = useStyles();
  const isMobile = useIsMobile();

  const [encounters, setEncounters] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [profMap, setProfMap] = useState({});
  const [loading, setLoading] = useState(false);

  // Carrega consultas + agenda ao abrir (best-effort; não trava o preview).
  useEffect(() => {
    if (!open || !patient || !patient.id) return undefined;
    let active = true;
    setLoading(true);
    setEncounters([]);
    setAppointments([]);
    (async () => {
      try {
        const [encRes, profRes] = await Promise.all([
          listEncounters(patient.id),
          listEncounterProfessionals()
        ]);
        let appts = [];
        try {
          const ar = await listPatientAppointments(patient.id);
          appts = (ar && ar.appointments) || [];
        } catch (e) {
          /* agenda best-effort */
        }
        if (!active) return;
        const map = {};
        ((profRes && profRes.professionals) || []).forEach((p) => {
          map[String(p.id)] = p.name;
        });
        setProfMap(map);
        setEncounters((encRes && encRes.encounters) || []);
        setAppointments(appts);
      } catch (e) {
        /* tolerante */
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [open, patient]);

  if (!patient) return null;

  const age = computeAge(patient.birthDate);
  const hab = habitsSummary(patient.habits);
  const hasAnamnese =
    (patient.medicationsCurrent && patient.medicationsCurrent.trim()) ||
    (patient.familyHistory && patient.familyHistory.trim()) ||
    (patient.personalHistory && patient.personalHistory.trim()) ||
    hab ||
    (patient.notes && patient.notes.trim());

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile} PaperProps={{ style: { overflowX: "hidden" } }}>
      {/* Cabeçalho com foto real do paciente */}
      <div className={classes.header}>
        <PatientPhoto patientId={patient.id} photoUrl={patient.photoUrl} size={84} />
        <div>
          <Typography variant="h6" className={classes.name}>
            {patient.fullName || "Paciente"}
          </Typography>
          <div className={classes.chips}>
            {patient.gender ? <Chip size="small" label={patient.gender} /> : null}
            {age !== null ? <Chip size="small" variant="outlined" label={`${age} anos`} /> : null}
          </div>
        </div>
      </div>

      <div className={classes.metaRow}>
        <span>🆔 {formatCpf(patient.cpf)}</span>
        <span>📞 {patient.phone || "—"}</span>
        <span>🎂 {fmtDate(patient.birthDate)}</span>
        {patient.email ? <span>✉️ {patient.email}</span> : null}
        {patient.rg ? <span>RG: {patient.rg}</span> : null}
      </div>

      {/* Alergias em destaque */}
      {patient.allergies && patient.allergies.trim() ? (
        <Paper className={classes.allergy} variant="outlined">
          <WarningIcon style={{ color: "#dc2626" }} />
          <div>
            <div className={classes.fieldLabel} style={{ color: "#dc2626" }}>Alergias</div>
            <div className={classes.fieldValue} style={{ fontWeight: 600 }}>{patient.allergies}</div>
          </div>
        </Paper>
      ) : (
        <div className={classes.noAllergy}>
          <CheckCircleOutlineIcon fontSize="small" />
          <Typography variant="body2">Nenhuma alergia conhecida registrada.</Typography>
        </div>
      )}

      <DialogContent className={classes.anamnese}>
        {/* Anamnese */}
        {hasAnamnese ? (
          <Grid container spacing={2}>
            <Field label="Medicações em uso" value={patient.medicationsCurrent} classes={classes} />
            <Field label="Hábitos" value={hab} classes={classes} />
            <Field label="Histórico familiar" value={patient.familyHistory} classes={classes} />
            <Field label="Histórico pessoal" value={patient.personalHistory} classes={classes} />
            <Field label="Observações" value={patient.notes} classes={classes} full />
          </Grid>
        ) : (
          <Typography variant="body2" className={classes.empty}>
            Anamnese ainda não preenchida.
          </Typography>
        )}

        <Divider style={{ margin: "12px 0 4px" }} />

        {/* Últimas consultas */}
        <Typography variant="subtitle2" className={classes.sectionTitle}>
          <DescriptionIcon fontSize="small" /> Últimas consultas
          {loading ? <CircularProgress size={14} /> : ` (${encounters.length})`}
        </Typography>
        {!loading && encounters.length === 0 ? (
          <Typography variant="body2" className={classes.empty}>Nenhuma consulta registrada.</Typography>
        ) : (
          encounters.slice(0, 3).map((e) => (
            <div key={e.id} className={classes.miniRow}>
              <div className={classes.miniTop}>
                <Typography variant="body2" style={{ fontWeight: 600 }}>{fmtDateTime(e.encounterDate)}</Typography>
                <Chip
                  size="small"
                  label={ENCOUNTER_STATUS_LABELS[e.status] || e.status}
                  variant={e.status === "signed" ? "default" : "outlined"}
                  style={e.status === "signed" ? { background: "#d1fae5", color: "#059669" } : undefined}
                />
              </div>
              <div className={classes.miniMeta}>
                {ENCOUNTER_TYPE_LABELS[e.encounterType] || e.encounterType || "Consulta"}
                {e.professionalId && profMap[String(e.professionalId)] ? ` · ${profMap[String(e.professionalId)]}` : ""}
                {e.assessment ? ` · ${String(e.assessment).slice(0, 60)}${String(e.assessment).length > 60 ? "…" : ""}` : ""}
              </div>
            </div>
          ))
        )}

        {/* Agenda */}
        <Typography variant="subtitle2" className={classes.sectionTitle}>
          <EventNoteIcon fontSize="small" /> Agenda
          {loading ? <CircularProgress size={14} /> : ` (${appointments.length})`}
        </Typography>
        {!loading && appointments.length === 0 ? (
          <Typography variant="body2" className={classes.empty}>Nenhum agendamento na agenda médica.</Typography>
        ) : (
          appointments.slice(0, 3).map((a) => (
            <div key={a.id} className={classes.miniRow}>
              <div className={classes.miniTop}>
                <Typography variant="body2" style={{ fontWeight: 600 }}>{fmtDateTime(a.datetimeStart)}</Typography>
                <Chip size="small" variant="outlined" label={apptStatusLabel[a.status] || a.status || "—"} />
              </div>
              <div className={classes.miniMeta}>
                {a.professionalName ? a.professionalName : "—"}
                {a.procedureType ? ` · ${a.procedureType}` : ""}
                {a.paymentType ? ` · ${a.paymentType === "convenio" ? (a.insuranceName || "Convênio") : "Particular"}` : ""}
              </div>
            </div>
          ))
        )}

        <Box mt={1.5}>
          <Typography variant="caption" color="textSecondary">
            Pré-visualização. Para editar, anexar exames/documentos e fotos, abra o prontuário completo.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Button color="primary" variant="contained" startIcon={<OpenInNewIcon />} onClick={onOpenFull}>
          Abrir prontuário completo
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientPreviewDialog;
